//@flow
import {of} from 'rxjs';
import {ActionsObservable, ofType, StateObservable} from 'redux-observable';
import {
  catchError,
  concatMap,
  debounceTime,
  map,
  switchMap,
} from 'rxjs/operators';

import {ActionCreator, createReducer, buildCatchError} from '../../store/utils';
import {
  editProductApi,
  getProductsApi,
  endpoints as productsEndpoints,
  addProductImageApi,
  editProductBulkApi,
} from '../products/api';
import {buildRoute} from '../../http';
import {uploadImageStoreApi} from '../file-storage/api';
import {createSingleFieldFormData} from '../../utils/form';

import type {
  Product,
  Pagination,
  StoredFile,
  ProductVariant,
  ProductDetailVariant,
  ProductVariantSetting,
  BulkEditProductRequest,
  BulkProductDetailVariant,
} from '../../entities';

import type {RootState} from '..';
import {getLocalStorage, updateLocalStorage} from '../../utils/dom';
import {List} from 'immutable';

export type FilterType =
  | 'category'
  | 'promotions'
  | 'wholesale-price'
  | 'stock';
export type BulkEditOptionType = 'price' | 'offer-price' | 'category' | 'stock';
export type BulkEditOption = {
  type?: BulkEditOptionType,
  value?: string,
};

type QueryFilter = {[key: string]: string};
export type BulkEditState = {
  selectedProducts: Product[],
  products: Product[],
  productVariants: ProductVariant[],
  selectedProductsVariants: BulkProductDetailVariant[],
  filter: {
    type: FilterType,
    value: string[],
  },
  isFiltering: boolean,
  bulkEditOption: BulkEditOption,
  tableActiveCells: string[],
  pagination?: Pagination,
  isFetchingProducts?: boolean,
  isEditingProduct?: boolean,
  isAddingImage?: boolean,
  successEditingProduct?: boolean,
  errorEditingProduct?: boolean,
};

const TABLE_ACTIVE_CELLS_KEY = 'bulk-edit-table-active-cells';

const defaultTableActiveCells = [
  'name',
  'video-link',
  'sku',
  'price',
  'taxed-price',
  'offerPrice',
  'taxed-offerPrice',
  'wholesale-price',
];

export const initialState: BulkEditState = {
  products: [],
  productVariants: [],
  selectedProducts: [],
  selectedProductsVariants: [],
  filter: {
    type: 'category',
    value: [],
  },
  isFiltering: false,
  bulkEditOption: {},
  tableActiveCells:
    getLocalStorage(TABLE_ACTIVE_CELLS_KEY) || defaultTableActiveCells,
};

export type BulkEditAction = {
  bulkEditProductRequest?: BulkEditProductRequest[],
  product?: Product,
  variant?: ProductDetailVariant,
  products?: Product[],
  productVariants?: ProductVariant[],
  updateVariantProduct?: ProductVariantSetting,
  limitedSelected?: number,
  tableCellId?: string,
  filter?: {
    type: FilterType,
    value: string[],
  },
  bulkEditOption?: BulkEditOption,
  queryFilter?: QueryFilter,
  page?: number,
  pagination?: Pagination,
  productRequest?: Product,
  productId?: string,
  variantId?: string,
  image?: File,
};

export class ToggleTableCellId extends ActionCreator<BulkEditAction> {}

export class AddWholesalePriceCell extends ActionCreator<BulkEditAction> {}

export class SetProductFilter extends ActionCreator<BulkEditAction> {}

export class SetBulkEditOption extends ActionCreator<BulkEditAction> {}

export class FetchBulkEditProducts extends ActionCreator<BulkEditAction> {}
export class FetchBulkEditProductsSuccess extends ActionCreator<BulkEditAction> {}
export class FetchBulkEditProductsError extends ActionCreator<BulkEditAction> {}

export class EditProductField extends ActionCreator<BulkEditAction> {}
export class EditProductFieldSuccess extends ActionCreator<BulkEditAction> {}
export class EditProductFieldError extends ActionCreator<BulkEditAction> {}

export class EditProductFieldBulk extends ActionCreator<BulkEditAction> {}
export class EditProductFieldBulkSuccess extends ActionCreator<BulkEditAction> {}

export class AddImageToProduct extends ActionCreator<BulkEditAction> {}
export class AddImageToProductSuccess extends ActionCreator<BulkEditAction> {}
export class AddImageToProductError extends ActionCreator<BulkEditAction> {}

export class ToggleProductSelection extends ActionCreator<BulkEditAction> {}
export class ToggleVariantSelection extends ActionCreator<BulkEditAction> {}
export class ToggleVariantsInProduct extends ActionCreator<BulkEditAction> {}
export class RemoveAllToggleProductSelection extends ActionCreator<BulkEditAction> {}

export class EditSelectedProducts extends ActionCreator<BulkEditAction> {}
export class DeleteSelectedProducts extends ActionCreator<BulkEditAction> {}

export class UpdateProductSelected extends ActionCreator<BulkEditAction> {}

export const actions = {
  fetchBulkEditProducts: (queryFilter: QueryFilter) =>
    new FetchBulkEditProducts({queryFilter}),
  searchBulkEditProducts: (queryFilter: QueryFilter) =>
    new FetchBulkEditProducts({queryFilter, page: 0}),
  toggleTableCellId: (tableCellId: string) =>
    new ToggleTableCellId({tableCellId}),
  addWholesalePriceCell: () => new AddWholesalePriceCell(),
  setProductFilter: (filter: {type: FilterType, value: string[]}) =>
    new SetProductFilter({filter}),
  setBulkEditOption: (bulkEditOption: BulkEditOption) =>
    new SetBulkEditOption({bulkEditOption}),
  editProductField: (product: Product, productId: string) =>
    new EditProductField({productRequest: product, productId}),
  editProductFieldBulk: (product: Product) =>
    new EditProductFieldBulk({productRequest: product}),

  addImageToProduct: (productId: string, image: File) =>
    new AddImageToProduct({productId, image}),

  toggleProductSelection: (product: Product) =>
    new ToggleProductSelection({product}),
  toggleVariantSelection: (variant: ProductDetailVariant, productId: string) =>
    new ToggleVariantSelection({variant, productId}),
  toggleVariantsInProduct: (product: Product, limitedSelected: number) =>
    new ToggleVariantsInProduct({product, limitedSelected}),
  removeAllToggleProductSelection: () => new RemoveAllToggleProductSelection(),
  deleteSelectedProducts: () => new DeleteSelectedProducts(),

  editSelectedProducts: (bulkEditProductRequest: BulkEditProductRequest[]) =>
    new EditSelectedProducts({bulkEditProductRequest}),
  updateProductSelected: (product: Product) =>
    new UpdateProductSelected({product}),
};

export default createReducer(initialState, {
  [ToggleTableCellId.type]: (
    state: BulkEditState,
    {tableCellId}: BulkEditAction
  ): BulkEditState => {
    const tableActiveCells = [...state.tableActiveCells];
    const index = tableActiveCells.indexOf(tableCellId);
    if (index > -1) {
      tableActiveCells.splice(index, 1);
    } else {
      tableCellId && tableActiveCells.push(tableCellId);
    }

    updateLocalStorage(TABLE_ACTIVE_CELLS_KEY, tableActiveCells);
    return {
      ...state,
      tableActiveCells,
    };
  },
  [AddWholesalePriceCell.type]: (state: BulkEditState): BulkEditState => {
    const tableActiveCells = [...state.tableActiveCells];
    const prevWholesalePriceCount = tableActiveCells.filter(id =>
      id.includes('wholesale-price-option')
    ).length;
    tableActiveCells.push(
      `wholesale-price-option-${prevWholesalePriceCount + 1}`
    );
    return {
      ...state,
      tableActiveCells,
    };
  },
  [SetProductFilter.type]: (
    state: BulkEditState,
    {filter}: BulkEditAction
  ): BulkEditState => {
    if (!filter) return state;
    return {
      ...state,
      filter,
    };
  },
  [SetBulkEditOption.type]: (
    state: BulkEditState,
    {bulkEditOption}: BulkEditAction
  ): BulkEditState => {
    if (!bulkEditOption) return state;
    return {
      ...state,
      bulkEditOption,
    };
  },
  [FetchBulkEditProducts.type]: (state: BulkEditState): BulkEditState => {
    return {
      ...state,
      isFetchingProducts: true,
    };
  },
  [FetchBulkEditProductsSuccess.type]: (
    state: BulkEditState,
    action: BulkEditAction
  ): BulkEditState => {
    return {
      ...state,
      isFetchingProducts: undefined,
      products: action.products || [],
      pagination: action.pagination,
    };
  },
  [FetchBulkEditProductsError.type]: (state: BulkEditState): BulkEditState => {
    return {
      ...state,
      isFetchingProducts: undefined,
    };
  },
  [EditProductFieldBulk.type]: (
    state: BulkEditState,
    {productRequest}: BulkEditAction
  ): BulkEditState => {
    return {
      ...state,
      isEditingProduct: true,
      successEditingProduct: undefined,
      errorEditingProduct: undefined,
    };
  },
  [EditProductFieldBulkSuccess.type]: (
    state: BulkEditState,
    {productRequest}: BulkEditAction
  ): BulkEditState => {
    const newVariantsSelected = state.selectedProductsVariants;
    const getDataProductRequest = productRequest?.products;
    const newProducts = state.products;

    newProducts.forEach((product, index) => {
      const productFound = getDataProductRequest?.find(
        product => product.id === getDataProductRequest?.productId
      );

      if (productFound) {
        const {variants, ...rest} = productFound;

        const updatedVariants = product.productVariantSetting.variants?.map(
          variant => {
            const matchingVariant = variants.find(
              v => v.variantId === variant.id
            );
            return matchingVariant ? {...variant, ...matchingVariant} : variant;
          }
        );

        newProducts[index] = {
          ...product,
          productVariantSetting: {
            ...product.productVariantSetting,
            variants: updatedVariants || [],
          },
          ...rest,
        };
      }
    });

    newVariantsSelected.forEach((variant, index) => {
      const variantFound = getDataProductRequest?.find(
        product => product.productId === variant.productId
      );

      if (variantFound) {
        const variantIndex = variantFound.variants.findIndex(
          v => v.variantId === variant.id
        );

        if (variantIndex !== -1) {
          const {offerPrice, ...rest} = variantFound.variants[variantIndex];
          newVariantsSelected[index] = {
            ...variant,
            ...rest,
          };
        }
      }
    });

    return {
      ...state,
      selectedProductsVariants: newVariantsSelected,
      products: newProducts,
      isEditingProduct: undefined,
      successEditingProduct: true,
      errorEditingProduct: undefined,
    };
  },
  [EditProductField.type]: (state: BulkEditState): BulkEditState => {
    return {
      ...state,
      isEditingProduct: true,
      successEditingProduct: undefined,
      errorEditingProduct: undefined,
    };
  },
  [EditProductFieldSuccess.type]: (state: BulkEditState): BulkEditState => {
    return {
      ...state,
      isEditingProduct: undefined,
      successEditingProduct: true,
      errorEditingProduct: undefined,
    };
  },
  [EditProductFieldError.type]: (state: BulkEditState): BulkEditState => {
    return {
      ...state,
      isEditingProduct: undefined,
      successEditingProduct: false,
      errorEditingProduct: true,
    };
  },
  [ToggleProductSelection.type]: (
    state: BulkEditState,
    {product}: BulkEditAction
  ): BulkEditState => {
    const selectedProducts = [...state.selectedProducts];
    const index = selectedProducts.findIndex(productSelected => {
      return productSelected.id === product?.id;
    });

    if (index > -1) {
      selectedProducts.splice(index, 1);
    } else {
      product && selectedProducts.push(product);
    }
    return {
      ...state,
      selectedProducts,
    };
  },
  [ToggleVariantSelection.type]: (
    state: BulkEditState,
    {variant, productId}: BulkEditAction
  ): BulkEditState => {
    const selectedProductsVariants = [...state.selectedProductsVariants];
    const index = selectedProductsVariants.findIndex(
      variantSelected => variantSelected.id === variant?.id
    );
    if (index > -1) {
      selectedProductsVariants.splice(index, 1);
    } else {
      const productsState = state.products;
      productsState.forEach(product => {
        if (product.id === productId) {
          // $FlowFixMe
          product.productVariantSetting.variants.forEach(variantProduct => {
            if (variantProduct.id === variant?.id) {
              selectedProductsVariants.push({
                ...variantProduct,
                price: variantProduct.price,
                offerPrice: variantProduct.offerPrice,
                productId,
              });
            }
          });
        }
      });
    }
    return {
      ...state,
      selectedProductsVariants,
    };
  },
  [ToggleVariantsInProduct.type]: (
    state: BulkEditState,
    {product, limitedSelected}: BulkEditAction
  ): BulkEditState => {
    let selectedProductsVariants = [...state.selectedProductsVariants];

    let limitedSelectedRedux = limitedSelected || 1;
    let totalSelected =
      state.selectedProducts.length + selectedProductsVariants.length;

    if (totalSelected >= limitedSelectedRedux) return state;

    // $FlowFixMe
    if (product?.enabledProductVariant === false) {
      return {
        ...state,
        selectedProductsVariants,
      };
    }

    state.selectedProducts.forEach(productSelected => {
      if (productSelected.id === product?.id) {
        const productHasVariants =
          product?.productVariantSetting?.variants || [];

        if (productHasVariants?.length > 0) {
          productHasVariants.forEach(variant => {
            const isVariantSelected = selectedProductsVariants.some(
              productVariantSelection =>
                productVariantSelection.id === variant.id
            );

            if (!isVariantSelected) {
              selectedProductsVariants.push({
                ...variant,
                productId: product?.id,
              });

              totalSelected++;
            }
          });
        }
      }
    });

    // selectedProductsVariants = selectedProductsVariants.filter(productVariant =>
    //   state.selectedProducts.some(
    //     productSelected => productSelected.id === productVariant.productId
    //   )
    // );

    selectedProductsVariants = selectedProductsVariants.filter(
      productVariant => {
        const isVariantOfProduct = productVariant.productId === product?.id;

        if (!isVariantOfProduct) {
          return true;
        }

        const isProductSelected = state.selectedProducts.some(
          productSelected => productSelected.id === productVariant.productId
        );

        return isProductSelected || !productVariant.productId;
      }
    );

    return {
      ...state,
      selectedProductsVariants,
    };
  },
  [RemoveAllToggleProductSelection.type]: (
    state: BulkEditState
  ): BulkEditState => {
    return {
      ...state,
      selectedProductsVariants: [],
      selectedProducts: [],
    };
  },
  [AddImageToProduct.type]: (state: BulkEditState): BulkEditState => {
    return {
      ...state,
      isAddingImage: true,
    };
  },
  [AddImageToProductSuccess.type]: (state: BulkEditState): BulkEditState => {
    return {
      ...state,
      isAddingImage: undefined,
    };
  },
  [AddImageToProductError.type]: (state: BulkEditState): BulkEditState => {
    return {
      ...state,
      isAddingImage: undefined,
    };
  },
  [DeleteSelectedProducts.type]: (state: BulkEditState): BulkEditState => {
    const selectedProducts = [...state.selectedProducts];
    const selectedProductsId = selectedProducts.map((pr: Object) =>
      pr.id.toString()
    );
    const products = state.products.filter(
      product => !selectedProductsId.includes(product.id)
    );

    return {
      ...state,
      products,
      selectedProducts: [],
    };
  },
  [UpdateProductSelected.type]: (
    state: BulkEditState,
    {product}: BulkEditAction
  ): BulkEditState => {
    const updatedProduct: Object = product;
    const index = state.selectedProducts.findIndex(
      (p: Object) => p.id === updatedProduct.id
    );

    if (index > -1) {
      state.selectedProducts[index] = updatedProduct;
    }
    return {
      ...state,
      selectedProducts: state.selectedProducts,
    };
  },
  [EditSelectedProducts.type]: (
    state: BulkEditState,
    {bulkEditProductRequest}: BulkEditAction
  ) => {
    if (!bulkEditProductRequest) return {...state};

    const productsSelected = List(state.selectedProducts);
    const newProducts = productsSelected.map(product => {
      const productRequest = bulkEditProductRequest.find(
        p => p.productId === product.id
      );

      if (!productRequest) return product;

      const categoryObjects = productRequest.categories?.map(category => ({
        id: category,
      }));

      const stockSetting = {
        enabled: product.stockSetting?.enabled,
        stock: productRequest.stock,
      };

      //$FlowFixMe
      return {
        ...product,
        ...productRequest,
        stockSetting: stockSetting,
        categories: categoryObjects,
      };
    });

    const newVariantsProducts = state.selectedProductsVariants.map(variant => {
      const productRequest = bulkEditProductRequest.find(p => {
        // $FlowFixMe
        return p.variants && p.variants.some(v => v.variantId === variant.id);
      });

      if (!productRequest) return variant;
      // $FlowFixMe
      const variantRequest = productRequest?.variants.find(
        v => v.variantId === variant.id
      );

      return {
        ...variant,
        ...variantRequest,
      };
    });

    return {
      ...state,
      selectedProducts: newProducts.toJS(),
      selectedProductsVariants: newVariantsProducts,
    };
  },
});

export function fetchBulkEditProductsEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(FetchBulkEditProducts.type),
    debounceTime(500),
    switchMap(({queryFilter}: BulkEditAction) => {
      return getProductsApi({...queryFilter, product_type: 'FULL'}).pipe(
        map(
          ({data, pagination}: {data: Product[], pagination: Pagination}) =>
            new FetchBulkEditProductsSuccess({
              products: data,
              pagination,
            })
        ),
        buildCatchError(
          FetchBulkEditProductsError,
          'Error al obtener productos'
        )
      );
    })
  );
}

export function editProductFieldEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(EditProductField.type),
    debounceTime(500),
    switchMap(({productRequest, productId}: BulkEditAction) => {
      if (!productRequest || !productId) {
        return of(new EditProductFieldError());
      }

      return editProductApi(productRequest, {
        route: buildRoute(productsEndpoints.EDIT_PRODUCT, [productId]),
      }).pipe(
        map(data => new EditProductFieldSuccess({productId: data.id})),
        buildCatchError(EditProductFieldError, 'Error al editar el producto')
      );
    })
  );
}

export function editProductFieldBulkEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(EditProductFieldBulk.type),
    debounceTime(500),
    switchMap(({productRequest, productId}: BulkEditAction) => {
      if (!productRequest) {
        return of(new EditProductFieldError());
      }

      return editProductBulkApi(productRequest).pipe(
        map(map => new EditProductFieldBulkSuccess({productRequest})),
        buildCatchError(EditProductFieldError, 'Error al editar el producto')
      );
    })
  );
}

export function changeProductImageEpic(
  action$: ActionsObservable,
  state$: StateObservable<RootState>
) {
  return action$.pipe(
    ofType(AddImageToProduct.type),
    switchMap(({productId, image}: BulkEditAction) => {
      if (!productId || !image) {
        return of(new AddImageToProductError());
      }

      const product = state$.value.bulkEdit.products.find(
        p => p.id === productId
      );

      if (!product) {
        return of(new AddImageToProductError());
      }

      const prevImages = product.images
        ? product.images.map(image => image.id)
        : [];

      return uploadImageStoreApi(
        createSingleFieldFormData(image, undefined, {
          filePrefix: 'im/product',
        })
      ).pipe(
        map((files: StoredFile[]) => [undefined, files, productId, prevImages]),
        catchError(() =>
          of([
            'Error al subir la imagen, por favor intente nuevamente',
            undefined,
            undefined,
            undefined,
          ])
        )
      );
    }),
    concatMap(
      ([error, files, productId, prevImages]: [
        ?string,
        StoredFile[],
        string,
        string[]
      ]) => {
        if (error) return of(new AddImageToProductError({error}));

        const uploaded = files.map(f => f.id);

        const fileIds = [...uploaded, ...prevImages];
        return addProductImageApi(
          {fileIds},
          {
            route: buildRoute(productsEndpoints.ADD_PRODUCT_IMAGE, [productId]),
          }
        ).pipe(
          map(param => {
            return new AddImageToProductSuccess();
          }),
          buildCatchError(AddImageToProductError, 'Error al editar el producto')
        );
      }
    )
  );
}
